// Shared presentational primitives for the card layouts on the positions,
// open-orders and trades surfaces. Pure UI — no data derivation lives here,
// so the table and card paths can't drift on what a number *means*.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Grid3x3, Info, LayoutGrid, List, Pin, Rows3 } from 'lucide-react';

/** Horizontal scroller with an ALWAYS-VISIBLE scrollbar and edge FADES that
 *  appear only when content actually extends past that edge — the off-screen
 *  card peeks through the gradient as an implicit "more this way" (Brendan,
 *  Jul 9). The scrollbar is OURS (track + draggable thumb below the content;
 *  native is hidden via .g1-hscroll): Chrome 150/macOS keeps overlay
 *  scrollbars that hide at rest no matter what ::-webkit-scrollbar or
 *  scrollbar-width say — verified live — so a persistent bar has to be an
 *  owned element. Callback ref, not an effect: the scroller mounts/unmounts
 *  with its section. */
export function EdgeFadeScroller({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const [edges, setEdges] = useState({ left: false, right: false });
  const [thumb, setThumb] = useState<{ widthPct: number; leftPct: number } | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    if (el.scrollWidth > el.clientWidth + 1) {
      const widthPct = (el.clientWidth / el.scrollWidth) * 100;
      const leftPct = (el.scrollLeft / el.scrollWidth) * 100;
      setThumb((prev) =>
        prev &&
        Math.abs(prev.widthPct - widthPct) < 0.1 &&
        Math.abs(prev.leftPct - leftPct) < 0.1
          ? prev
          : { widthPct, leftPct },
      );
    } else {
      setThumb(null);
    }
  }, []);
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      roRef.current?.disconnect();
      roRef.current = null;
      scrollerRef.current = el;
      if (!el) return;
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      // The content wrapper too — cards/rows appearing changes scrollWidth
      // without touching the scroller's own box.
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      roRef.current = ro;
    },
    [update],
  );
  const fade =
    'pointer-events-none absolute top-0 w-12 transition-opacity duration-200';
  return (
    <div className={`relative ${className}`}>
      {/* pb-1 = the gutter between content and the bar below. */}
      <div ref={attach} onScroll={update} className="g1-hscroll overflow-x-auto pb-1">
        {children}
      </div>
      <div
        aria-hidden="true"
        className={`${fade} bottom-2.5 left-0 bg-gradient-to-r from-background via-background/60 to-transparent ${
          edges.left ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        aria-hidden="true"
        className={`${fade} bottom-2.5 right-0 bg-gradient-to-l from-background via-background/60 to-transparent ${
          edges.right ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {thumb && (
        <div
          ref={trackRef}
          onPointerDown={(e) => {
            // Track click (not thumb) — jump so the click point centers.
            const el = scrollerRef.current;
            const track = trackRef.current;
            if (!el || !track || e.target !== track) return;
            const rect = track.getBoundingClientRect();
            const frac = (e.clientX - rect.left) / rect.width;
            el.scrollLeft = frac * el.scrollWidth - el.clientWidth / 2;
          }}
          className="bg-border/25 relative mt-1 h-1.5 w-full cursor-pointer rounded-full"
        >
          <div
            role="scrollbar"
            aria-controls=""
            aria-orientation="horizontal"
            aria-valuenow={Math.round(thumb.leftPct)}
            onPointerDown={(e) => {
              const el = scrollerRef.current;
              if (!el) return;
              e.preventDefault();
              e.stopPropagation();
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              dragRef.current = { startX: e.clientX, startScrollLeft: el.scrollLeft };
            }}
            onPointerMove={(e) => {
              const d = dragRef.current;
              const el = scrollerRef.current;
              const track = trackRef.current;
              if (!d || !el || !track) return;
              el.scrollLeft =
                d.startScrollLeft +
                (e.clientX - d.startX) * (el.scrollWidth / track.clientWidth);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
            className="bg-border hover:bg-muted-foreground/60 absolute inset-y-0 cursor-grab rounded-full active:cursor-grabbing"
            style={{ width: `${thumb.widthPct}%`, left: `${thumb.leftPct}%` }}
          />
        </div>
      )}
    </div>
  );
}

/** Responsive grid that holds a set of entity cards. */
export function CardGrid({
  children,
  className = '',
  variant = 'default',
}: {
  children: ReactNode;
  className?: string;
  /** 'mini' = dense auto-fill grid (~12 across on a wide monitor). */
  variant?: 'default' | 'mini';
}) {
  return (
    <div
      className={`g1-card-grid-cq ${variant === 'mini' ? 'g1-card-grid-cq--mini' : ''} ${className}`}
    >
      <div className="g1-card-grid-cq__grid">{children}</div>
    </div>
  );
}

/** Dense, no-gutter grid for the PositionTracker compact-list view.
 *  The min track width is about two normal position cards, so a wide
 *  six-card row becomes three compact columns. */
export function CompactListGrid({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`g1-compact-list-cq ${className}`}>
      <div className="g1-compact-list-cq__grid">{children}</div>
    </div>
  );
}

/** The card frame. `standout` draws the amber border used for orders that
 *  are actively filling, so they pop out of the grid. */
export function CardShell({
  standout = false,
  accentClass = '',
  className = '',
  title,
  ariaLabel,
  onClick,
  children,
}: {
  standout?: boolean;
  accentClass?: string;
  className?: string;
  title?: string;
  ariaLabel?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onClick || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      title={title}
      role={onClick ? 'link' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`bg-card relative flex flex-col overflow-hidden rounded-lg border p-3 transition-colors ${
        standout
          ? 'border-amber-400 ring-2 ring-amber-400/30'
          : 'border-border hover:border-muted-foreground/30'
      } ${onClick ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70' : ''} ${className}`}
    >
      {accentClass && (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-y-0 left-0 w-1 ${accentClass}`}
        />
      )}
      {children}
    </div>
  );
}

/** Card header: two badge clusters (left/right), title, optional subtitle. */
export function CardHeader({
  leftBadges,
  rightBadges,
  title,
  subtitle,
}: {
  leftBadges?: ReactNode;
  rightBadges?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <div className="mb-2">
      {(leftBadges || rightBadges) && (
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1">{leftBadges}</div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            {rightBadges}
          </div>
        </div>
      )}
      {/* truncate + min-w-0 — without these, an unbroken string (e.g. a
          Polymarket asset_id hex when the market lookup hasn't resolved
          yet) would push the card's content box wider than the grid track
          and visually disrupt the row. Tooltip on the wrapper surfaces the
          full string on hover for power users. */}
      <div
        className="text-foreground min-w-0 truncate text-sm font-semibold leading-tight"
        title={typeof title === 'string' ? title : undefined}
      >
        {title}
      </div>
      {subtitle && (
        <div
          className="text-muted-foreground mt-0.5 min-w-0 truncate font-mono text-[11px] leading-tight"
          title={typeof subtitle === 'string' ? subtitle : undefined}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}

function findTeamToken(title: string, team: string): { start: number; end: number } | null {
  const haystack = title.toUpperCase();
  const needle = team.toUpperCase();
  let from = 0;
  while (from < haystack.length) {
    const start = haystack.indexOf(needle, from);
    if (start === -1) return null;
    const end = start + needle.length;
    const before = start === 0 ? '' : haystack[start - 1];
    const after = end >= haystack.length ? '' : haystack[end];
    const beforeOk = before === '' || !/[A-Z0-9]/.test(before);
    const afterOk = after === '' || !/[A-Z0-9]/.test(after);
    if (beforeOk && afterOk) return { start, end };
    from = end;
  }
  return null;
}

/** Highlights the market's contract team inside matchup titles, without using
 * YES/NO colors. A NO on WSH still highlights WSH as the contract team while
 * the side badge carries the red NO semantics. */
export function BetTeamTitle({
  title,
  team,
}: {
  title: string;
  team?: string | null;
}) {
  const match = team ? findTeamToken(title, team) : null;
  if (!match) return <span title={title}>{title}</span>;

  return (
    <span title={title}>
      {title.slice(0, match.start)}
      <span className="rounded bg-primary/15 px-1 text-primary">
        {title.slice(match.start, match.end)}
      </span>
      {title.slice(match.end)}
    </span>
  );
}

/** Card body — grows to equalise card heights so footers line up in a row. */
export function CardBody({ children }: { children: ReactNode }) {
  return <div className="flex-1">{children}</div>;
}

/** Divider + footer region (cost / velocity / age live here). */
export function CardFooter({ children }: { children: ReactNode }) {
  return <div className="border-border/60 mt-2 border-t pt-2">{children}</div>;
}

/** A label/value line inside a card body. */
export function StatLine({
  label,
  value,
  valueClass = '',
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={`font-mono text-sm ${valueClass}`}>{value}</span>
    </div>
  );
}

/** Label-over-value cell for the dense compact-item stat rows
 *  (CompactPositionItem / CompactOrderItem). */
export function CompactStat({
  label,
  value,
  valueClass = 'text-foreground',
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground truncate text-[10px] leading-none">{label}</div>
      <div className={`mt-0.5 truncate font-mono text-[11px] font-semibold leading-tight ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}

/** Thumbtack toggle for the PositionTracker's Pinned strip. Pinning makes a
 *  WATCH COPY — the item stays in its normal section too — so the tooltip
 *  says so. stopPropagation: a pin click must never fire the row/card
 *  onOpen underneath it. */
export function PinToggle({
  pinned,
  onToggle,
  size = 12,
}: {
  pinned: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={
        pinned
          ? 'Unpin from the Pinned section'
          : 'Pin to the Pinned section (a watch copy — the item stays here too)'
      }
      aria-pressed={pinned}
      className={`shrink-0 rounded p-1 transition-colors ${
        pinned
          ? 'text-amber-400 hover:bg-amber-400/10'
          : 'text-muted-foreground hover:bg-amber-400/10 hover:text-amber-400'
      }`}
    >
      <Pin size={size} className={pinned ? 'fill-current' : undefined} />
    </button>
  );
}

/** Small uppercase pill badge. */
export function Pill({
  children,
  className = '',
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

/** YES/NO side badge, optionally prefixed with the market's named team. */
export function SideBadge({
  side,
  team,
}: {
  side: 'yes' | 'no';
  team?: string | null;
}) {
  return (
    <Pill
      className={
        side === 'yes'
          ? 'bg-green-500/20 text-green-400'
          : 'bg-red-500/20 text-red-400'
      }
    >
      {team ? `${team} ` : ''}
      {side.toUpperCase()}
    </Pill>
  );
}

/** One tile in the summary strip above a card grid (e.g. "open orders 9"). */
export function SummaryTile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'positive' | 'warning' | 'danger';
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-positive'
      : tone === 'warning'
        ? 'text-amber-400'
        : tone === 'danger'
          ? 'text-negative'
          : 'text-foreground';
  return (
    <div className="bg-card border-border rounded-lg border px-4 py-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

/** Strip wrapper for SummaryTiles. */
export function SummaryStrip({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-3">
      {children}
    </div>
  );
}

type ViewModeToggleValue = 'compact' | 'table' | 'card' | 'mini';

const VIEW_MODE_META: Record<ViewModeToggleValue, {
  title: string;
  ariaLabel: string;
  icon: typeof List;
}> = {
  compact: {
    title: 'Compact list view',
    ariaLabel: 'Compact list view',
    icon: List,
  },
  table: {
    title: 'Table view',
    ariaLabel: 'Table view',
    icon: Rows3,
  },
  card: {
    title: 'Card view',
    ariaLabel: 'Card view',
    icon: LayoutGrid,
  },
  mini: {
    title: 'Mini card view (dense)',
    ariaLabel: 'Mini card view',
    icon: Grid3x3,
  },
};

/** Segmented view-mode toggle for dense list / table / card surfaces. */
export function ViewModeToggle<T extends ViewModeToggleValue>({
  value,
  onChange,
  modes,
}: {
  value: T;
  onChange: (m: T) => void;
  modes: readonly T[];
}) {
  const btn = (active: boolean) =>
    `flex items-center justify-center px-2 py-1 transition-colors ${
      active
        ? 'bg-blue-500/20 text-blue-400'
        : 'text-muted-foreground hover:text-foreground'
    }`;
  return (
    <div className="border-border inline-flex overflow-hidden rounded-md border">
      {modes.map((mode) => {
        const meta = VIEW_MODE_META[mode];
        const Icon = meta.icon;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => onChange(mode)}
            className={btn(value === mode)}
            title={meta.title}
            aria-label={meta.ariaLabel}
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );
}

/** Small info icon that toggles a click-dismissed popover with section help.
 *  Use next to a section header (Open Positions / Open Orders / My Trades)
 *  to explain the columns/badges/colors. Dismissed on outside-click or Esc. */
export function InfoTooltip({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-0.5 transition-colors"
        aria-label={title ? `About ${title}` : 'About this section'}
        aria-expanded={open}
      >
        <Info size={14} />
      </button>
      {open && (
        <div
          role="dialog"
          className="bg-card border-border absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border p-3 shadow-lg"
        >
          {title && (
            <div className="text-foreground mb-2 text-sm font-semibold">
              {title}
            </div>
          )}
          <div className="text-muted-foreground text-xs leading-relaxed">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
